/**
 * ThankYouDialog.jsx — small modal for a writer to send a thank-you
 * notification to a tipper (single or bulk-window).
 *
 * Three preset messages plus a custom textarea (max 240 chars in UI,
 * backend caps at 500 to leave headroom). On submit, calls onSubmit
 * with `{ preset, message }` — caller decides which endpoint to hit.
 */
import React, { useState } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';

const PRESETS = [
  { key: 'short', label: 'Short',  text: 'Thanks for the tip — really appreciated.' },
  { key: 'warm',  label: 'Warm',   text: 'Your tip just made my day. Thank you for backing my work — it genuinely keeps me writing.' },
  { key: 'fan',   label: 'Fan-club', text: 'VIP energy 💛 — thank you for the boost. New chapters are coming, and you\'ll be first to read them.' },
];
const MAX_LEN = 240;

const ThankYouDialog = ({
  open, onOpenChange,
  recipientName,           // string for single, "all 3 tippers this week" for bulk
  busy = false,
  onSubmit,                // (payload: {preset, message}) => Promise
  title = 'Send a thank-you',
  description = 'Send a quick note — they\'ll see it as an in-app notification.',
}) => {
  const [mode, setMode] = useState('preset');           // 'preset' | 'custom'
  const [preset, setPreset] = useState('warm');
  const [message, setMessage] = useState('');

  const canSubmit = !busy && (mode === 'preset' || message.trim().length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const payload = mode === 'custom'
      ? { message: message.trim().slice(0, MAX_LEN) }
      : { preset };
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-white max-w-lg"
        data-testid="thankyou-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-pink-400" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {description}
            {recipientName ? <span className="block mt-1 text-slate-300">To: <strong>{recipientName}</strong></span> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Mode toggle */}
          <div className="inline-flex rounded-full bg-slate-800 p-1 text-xs">
            <button
              type="button"
              onClick={() => setMode('preset')}
              className={`px-3 py-1 rounded-full transition-colors ${
                mode === 'preset' ? 'bg-pink-500 text-white' : 'text-slate-300'
              }`}
              data-testid="thankyou-mode-preset"
            >
              Preset
            </button>
            <button
              type="button"
              onClick={() => setMode('custom')}
              className={`px-3 py-1 rounded-full transition-colors ${
                mode === 'custom' ? 'bg-pink-500 text-white' : 'text-slate-300'
              }`}
              data-testid="thankyou-mode-custom"
            >
              Custom
            </button>
          </div>

          {mode === 'preset' ? (
            <div className="space-y-2">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => setPreset(p.key)}
                  className={`w-full text-left rounded-lg p-3 border transition-colors ${
                    preset === p.key
                      ? 'border-pink-400 bg-pink-500/10'
                      : 'border-slate-700 bg-slate-900/40 hover:border-slate-500'
                  }`}
                  data-testid={`thankyou-preset-${p.key}`}
                >
                  <div className="text-xs uppercase tracking-wider text-pink-300">{p.label}</div>
                  <div className="text-sm text-slate-200 mt-0.5">{p.text}</div>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
                placeholder="Write your own thank-you…"
                rows={4}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="thankyou-custom-input"
              />
              <div className="text-[11px] text-slate-400 mt-1 text-right">
                {message.length}/{MAX_LEN}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="border-slate-600 text-slate-200"
            data-testid="thankyou-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-pink-500 hover:bg-pink-400 text-white font-semibold disabled:opacity-50"
            data-testid="thankyou-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
            Send thanks
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ThankYouDialog;
